/**
 * 播放器错误码映射
 * 参考 tv-linux/src/views/newParticular/const.js
 * 根据错误码返回相应的错误提示信息
 */

export interface ErrorInfo {
  message: string;
  showIcon?: boolean;
  showRefresh?: boolean;
  showQRCode?: boolean;
  showErrCode?: boolean;
  errCode?: string | number;
  playErrCode?: string | number;
  errType?: string;
}

/**
 * 特殊错误码映射
 * 参考 tv-linux/src/views/newParticular/const.js:14-126
 */
const specialErrorCodeMap: Record<string | number, ErrorInfo> = {
  // 69.x 系列错误（播放器内核错误）
  '69.1': {
    message: 'playErrVideo',
    showErrCode: true,
  },
  '69.2': {
    message: 'playErrDefn',
    showErrCode: true,
    errCode: '69.2',
  },
  '69.4': {
    message: 'playErrNotTV',
    showErrCode: true,
    errCode: '69.4',
    showQRCode: true,
  },
  '69.6': {
    message: 'playErrDefn',
    showRefresh: false,
    showErrCode: true,
    errCode: '69.6',
  },
  '69.7': {
    message: 'playErrContentFail',
    showRefresh: true,
    showErrCode: true,
    errCode: '69.7',
  },
  // 80 系列错误（版权相关）
  80: {
    message: 'noCopyright',
    showRefresh: false,
  },
  '80.1': {
    message: 'regionNoCopyright',
    showRefresh: false,
  },
  '80.14': {
    message: 'notAllowOnTV',
    showRefresh: false,
  },
  // 84 错误
  84: {
    message: 'sensitiveInfo',
    showRefresh: false,
  },
  // -11111 错误
  '-11111': {
    message: 'encryptedVideo',
    showRefresh: false,
  },
};

/**
 * 网络错误码列表
 * 参考 tv-linux/src/views/newParticular/const.js:3
 */
const networkErrorCodes = [1, 2, 3, 4, 5, 30, 40, 41, 42, 50, 444, 445];

/**
 * 外星人错误码列表（内容被劫走）
 * 参考 tv-linux/src/views/newParticular/const.js:5
 */
const alienErrorCodes = [51, 52, 63, 65, 66, 79, 71, 73, 74, 76, 77, 81, 82, 86, 94];

/**
 * 限制错误码列表
 * 参考 tv-linux/src/views/newParticular/const.js:7
 */
const limitErrorCodes = [64, 100];

/**
 * 根据错误码获取错误信息
 * @param code 错误码（可以是数字或字符串，如 80 或 '80.1'）
 * @param exem 扩展错误码（可选，用于组合错误码如 '80.1'）
 * @returns 错误信息对象
 */
export function getErrorInfo(code: string | number, exem?: string | number): ErrorInfo {
  // 尝试组合错误码（如 '80.1'）
  const combinedCode = exem ? `${code}.${exem}` : String(code);
  // if( code === 61 ) {
  //   return {
  //     errType: ''
  //   }
  // }
  // 先检查特殊错误码映射
  if (specialErrorCodeMap[combinedCode]) {
    return specialErrorCodeMap[combinedCode];
  }

  // 检查特殊错误码（不带扩展码）
  if (specialErrorCodeMap[code]) {
    return specialErrorCodeMap[code];
  }

  // 检查网络错误
  const codeNum = typeof code === 'string' ? parseInt(code, 10) : code;
  if (!isNaN(codeNum) && networkErrorCodes.includes(codeNum)) {
    return {
      message: 'networkError',
      showIcon: true,
      showRefresh: true,
    };
  }

  // 检查外星人错误（内容被劫走）
  if (!isNaN(codeNum) && alienErrorCodes.includes(codeNum)) {
    return {
      message: 'alienError',
      showIcon: true,
      showRefresh: false,
    };
  }

  // 检查限制错误
  if (!isNaN(codeNum) && limitErrorCodes.includes(codeNum)) {
    return {
      message: 'limit',
      showIcon: true,
      showRefresh: false,
    };
  }

  // 默认错误信息
  return {
    message: 'alienError',
    showIcon: true,
    showRefresh: false,
  };
}

/**
 * 判断是否为网络错误
 */
export function isNetworkError(code: string | number): boolean {
  const codeNum = typeof code === 'string' ? parseInt(code, 10) : code;
  return !isNaN(codeNum) && networkErrorCodes.includes(codeNum);
}

/**
 * 判断是否为可重试的错误
 */
export function isRetryableError(code: string | number): boolean {
  const errorInfo = getErrorInfo(code);
  return errorInfo.showRefresh === true || isNetworkError(code);
}
